import type { RoomsParticipantDistribution } from '@/domain/shared/types'
import { CollectionId } from '@/scopes/common/components/CollectionCard/types'
import { FilterOption } from '@/scopes/search/types'

// eslint-disable-next-line unused-imports/no-unused-vars
export interface ValueFilter<V> {}

export interface SelectFilter<V, M = object> {
  options: FilterOption<V, M>[]
}

export interface MultiSelectFilter<V, M = object> {
  options: FilterOption<V, M>[]
}

export type SelectedValue<T> = T extends ValueFilter<infer U> ? U : never
export type SelectedOptionValue<T> = T extends SelectFilter<infer U> ? U : never
export type SelectedMultiOptionValues<T> = T extends MultiSelectFilter<infer U> ? U[] : never

export type SelectedOption<T> = T extends SelectFilter<infer U, infer M> ? FilterOption<U, M>[] : never
export type SelectedMultiOption<T> = T extends MultiSelectFilter<infer U, infer M> ? FilterOption<U, M>[] : never

export type FiltersSelection<F> = Partial<{
  [K in keyof F]: SelectedValue<F[K]> | SelectedOptionValue<F[K]> | SelectedMultiOptionValues<F[K]>;
}>

export interface SearchCriteria<F> {
  pagination?: SearchPagination
  sorting?: SearchSorting
  filterSelection?: FiltersSelection<F>
  requiredFilters?: SearchFilterType[]
  accommodationIds?: string[]
}

export type Participants = ValueFilter<RoomsParticipantDistribution>

/**
 * Search types
 */
export interface SearchValueFilters {
  PricePerPerson: ValueFilter<[number, number | null]>
  Participants: Participants
}

export interface SearchSelectFilters {
  Lastminute: SelectFilter<string, { DaysToDepartureRange: string }>
  Stars: SelectFilter<string>
  MetersToBeach: SelectFilter<string>
  MetersToCenter: SelectFilter<string>
  RoomCount: SelectFilter<string>
  TransportType: SelectFilter<string>
  CustomersRating: SelectFilter<string>
  HasAvailability: SelectFilter<string>
  HasAvailabilityNextSeason: SelectFilter<string>
  MetersToSlope: SelectFilter<string>
  MetersToSkiLift: SelectFilter<string>
  SkiAreaMinimumAltitudeMeters: SelectFilter<string>
  BedsInLivingroom: SelectFilter<string>
  BathroomCount: SelectFilter<string>
  SkiAreaSlopeKms: SelectFilter<string>
  IsFlightOnly: SelectFilter<string>
}

export const SingleSelectKeys = [
  'Lastminute',
  'Stars',
  'MetersToBeach',
  'MetersToCenter',
  'RoomCount',
  'TransportType',
  'CustomersRating',
  'HasAvailability',
  'HasAvailabilityNextSeason',
  'MetersToSlope',
  'MetersToSkiLift',
  'SkiAreaMinimumAltitudeMeters',
  'BedsInLivingroom',
  'BathroomCount',
  'SkiAreaSlopeKms',
  'IsFlightOnly',
] as (keyof SearchSelectFilters)[]

export interface SearchMultiSelectFilters {
  AccommodationType: MultiSelectFilter<string>
  Airline: MultiSelectFilter<string>
  ArrivalAirport: MultiSelectFilter<string>
  Campaigns: MultiSelectFilter<string>
  ChildrenFacilities: MultiSelectFilter<string>
  City: MultiSelectFilter<string, { ParentId: string }>
  Country: MultiSelectFilter<string>
  Collection: MultiSelectFilter<string>
  DepartureDate: MultiSelectFilter<string>
  DepartureAirport: MultiSelectFilter<string>
  Discount: MultiSelectFilter<string>
  Duration: MultiSelectFilter<string>
  Facilities: MultiSelectFilter<string>
  InternetFacilities: MultiSelectFilter<string>
  Mealplan: MultiSelectFilter<string>
  Region: MultiSelectFilter<string, { ParentId: string }>
  Subregion: MultiSelectFilter<string, { ParentId: string }>
  Themes: MultiSelectFilter<string>
  CampaignsAndDiscounts: MultiSelectFilter<string>
}

export const MultiSelectKeys = [
  'AccommodationType',
  'Airline',
  'ArrivalAirport',
  'Campaigns',
  'ChildrenFacilities',
  'City',
  'Country',
  'Collection',
  'DepartureDate',
  'DepartureAirport',
  'Discount',
  'Duration',
  'Facilities',
  'InternetFacilities',
  'Mealplan',
  'Region',
  'Subregion',
  'Themes',
  'CampaignsAndDiscounts',
] as (keyof SearchMultiSelectFilters)[]

export type SearchFilters = Partial<SearchValueFilters & SearchSelectFilters & SearchMultiSelectFilters>

export interface SearchResponse {
  results: SearchResult[]
  totalResults: number
  pagination: SearchPagination
  filters: SearchFilters
  filteredCollection?: {
    collection: Collection
    description: string
    image: string
    title: string
    usps: string[]
  }
  accommodationTips: AccommodationTip[]
  ga4FilterCollection: GA4FilterCollectionEvent
  ga4Impressions: unknown[]
}

export interface SearchPagination {
  offset: number
  limit: number
}

export interface SearchSorting {
  by: string
}

export interface Collection {
  descriptionForAccommodationPage: string
  id: CollectionId
  image: Image
  name: string
  text: string
  titleForAccommodationPage: string
  usps: string[]
}

export interface CollectionTeaser {
  collection: Collection
  image: string
  text: string
  title: string
  usps: string[]
}

export type SearchFilterSelection = FiltersSelection<SearchFilters>

export type SearchValueFilterType = keyof SearchValueFilters
export type SearchSelectFilterType = keyof SearchSelectFilters
export type SearchMultiSelectFilterType = keyof SearchMultiSelectFilters

export type SearchFilterType = keyof SearchFilters

/**
 * API types
 */
export interface SearchAPIRawResponse {
  pagination: {
    offset: number
    limit: number
    totalResults: number
  }
  filters: SearchAPIFilter[]
  sortInfo: { property: string }
  results: SearchResult[]
  accoTips?: AccommodationTipsApiResponse[]
  collectionTeaser?: CollectionTeaser
}

export interface SearchAPIFilter {
  type: keyof SearchFilters
  isMandatory?: boolean
  isMultiselectable?: boolean
  parentFilterType?: string
  values: SearchAPIFilterValue[]
}

export interface SearchAPIFilterValue {
  isAvailable?: boolean
  isSelected: boolean
  caption: string
  value: string
  count?: number
  metadata?: string
  groupInfo?: string
}

export interface Campaign {
  id: string
  title: string
  icon: string
  highlightedWord: string
}

export interface SearchFeaturedFilters {
  duration: string
  occupancy: string
  mealplan: string
}

export interface SearchGeolocation {
  latitude: number
  longitude: number
}

export interface SearchHighestRatedCategory {
  name: number
  rating: number
}

export interface SearchOccupancyInfo {
  adultsCount: number
  childrenCount: number
  babiesCount: number
}

export interface SearchResult {
  agnosticId: number
  arrivalDateDetail: string
  campaign?: Campaign
  collection?: Collection
  departureDateDetail: string
  deskId: number
  featuredFilters?: SearchFeaturedFilters
  geolocation: SearchGeolocation
  highestRatedCategory: SearchHighestRatedCategory
  id: number
  images: string[]
  isFlightOnly: boolean
  isFavorite: boolean
  isHighlighted: boolean
  isOutOfSeason?: boolean
  isUnavailable: boolean
  locations: SearchLocation[]
  name?: string
  occupancyInfo?: SearchOccupancyInfo
  price?: SearchResultPrice
  isMarketExclusive: boolean
  isSustainable: boolean
  rating?: number
  themes: string[]
  transportType: string
  url: string
  urlWithFilters: string
  userRating: number
  userRatingText: string
  usps: string[]
  outboundDepartureAirportCode: string
  outboundDepartureAirportName: string
}

export interface AcmInformation {
  transportType: string
  departureDateRaw: string
  durationRaw: number
  birthdates?: string[]
  mealplanCode?: string
  location?: string
  acmUrl: string
}

export interface PublicPrice {
  price?: number
  discountPercentage: number
}

export interface PriceDiscounts {
  originalPrice: number // will be deprecated
  originalAveragePrice: number
  originalTotalPrice: number
  discountPercentage: number
  discountType?: string
  description?: string
}

export interface YieldCampaignLogo {
  alt: string
  border: string
  class: string
  height: number
  hSpace: number
  src: string
  vSpace: number
  width: number
  mediaId: string
  title: string
  language: {
    Name: string
  }
  mediaExists: boolean
}

export interface SearchResultPrice {
  averagePrice: number
  totalPrice: number
  publicPrice?: PublicPrice
  skiPassPublicPrice?: number
  skiPassPublicPriceText?: string
  priceDiscounts?: PriceDiscounts
  currencySettings: {
    symbol: string
    symbolPosition: string
    numberOfDecimals: number
  }
  includedServices?: IncludedServices
  acmInformation?: AcmInformation
  yieldCampaignLogo?: YieldCampaignLogo
  mandatoryExtraCostsText?: string
  staticText?: string
  yieldCampaignId?: string
  yieldCampaignName?: string
  onRequest: boolean
}

export interface AccommodationTipsApiResponse {
  arrivalDateDetail: string
  countryName: string
  departureDateDetail: string
  featuredFilters: {
    occupancy: string
    mealplan: string
  }
  id: number
  images: string[]
  locations: SearchLocation[]
  name: string
  price?: {
    acmInformation?: AcmInformation
    averagePrice: number
  }
  rating: number
  transportType: string
  urlWithFilters: string
  userRating: number
  userRatingText: string
  outboundDepartureAirportName: string
}

export interface AccommodationTip {
  arrivalDate: string
  departureDate: string
  id: number
  images: string[]
  locations: SearchLocation[]
  mealPlan: string
  name: string
  occupancy: string
  price?: number
  totalPrice?: number
  rating: number
  url: string
  userRating: number
  userRatingText: string
  outboundDepartureAirportName: string
  acmInformation?: AcmInformation
}

export interface IncludedServices {
  accommodation?: string
  bus?: string
  carrental?: string
  flight?: string
  skimaterial?: string
  skipass?: string
  transfer?: string
}

export interface SearchLocation {
  deskId: number
  name?: string
  type?: 'country' | 'region' | 'subregion' | 'city'
  url?: string
  englishName?: string
}

interface Image {
  alt: string
  attributes: string
  brand: string
  dynamicRatios: Ratio[]
  extraClasses: string
  keepOriginalSize: boolean
  placeholderSrc: string
  ratio: Ratio
  resolve: string
  resolvedSrc: string
  sync: boolean
  title: string
  track: string
}

interface Ratio {
  breakpoint: string
  hasRatio: boolean
  height: number
  width: number
}

export interface GA4FilterCollectionEvent {
  'partyComposition'?: string
  'country'?: string
  'region'?: string
  'city'?: string
  'departureDate'?: string
  'duration'?: string
  'price'?: string
  'airports'?: string
  'boardType'?: string
  'number of rooms'?: string
  'bathroomCount'?: string
  'general facility'?: string
  'internet facility'?: string
  'children facility'?: string
  'recommended'?: string
  'campaigns'?: string
  'themes'?: string
  'departureWeeks'?: string
  'accoType'?: string
  'ratings'?: string
  'distance from city center'?: string
  'distance from sea'?: string
  'discount'?: string
  'beds in livingroom'?: string
  'has availability'?: string
  'flight only'?: string
  'stars'?: string
  'labels'?: string
  'transport'?: string
  'airline'?: string
  'destination'?: string
  'arrival airports'?: string
}
